import React, { useCallback, useContext, useEffect, useMemo, useState } from "react";
import { useSelector } from 'react-redux';
import { Spin, Table, message } from "antd";
import { LoadingOutlined } from "@ant-design/icons";
import { capitalize } from 'lodash';
import { TablePreferencesContext } from '../../context/TablePreferencesProvider';
import { history } from "../../helpers";

import { CaseFilterMap, CaseQueryParamMap } from "../../constants/CaseFilter"
import { buildSortableColumns } from '../../utils/tableColumns';
import { dateTimeFormatter } from "../../utils/dateFormatter";
import { createCaseFilterQueryParamMap, createTableQueryParamMap, createTableSortByMap } from "../../utils/createCaseFilterQueryParamMap";
import PhysicianAPI from '../../axios/physician/PhysicianApi'
import SearchAndFilterCases from "../../components/SearchAndFilterCases/SearchAndFilterCases";

const hideFields = Object.freeze([CaseFilterMap.PHYSICIAN_ID]);

const TableDataMap = {
  CASE_ID: CaseFilterMap.CASE_ID,
  PATIENT_NAME: `${CaseFilterMap.PATIENT_FIRST_NAME} ${CaseFilterMap.PATIENT_LAST_NAME}`,
  TELEMEDICINE_CATEGORY_ID: CaseFilterMap.TELEMEDICINE_CATEGORY_ID,
  CREATED_DATE: CaseFilterMap.CREATED_DATE,
  UPDATED_DATE: CaseFilterMap.UPDATED_DATE,
  COMPLETED_AT: 'completedAt',
  STATUS: CaseFilterMap.STATUS,
}

const TableSortByMap = createTableSortByMap(TableDataMap);

const createTableColumns = (sortOrder, sortBy) => buildSortableColumns(
  [
    { title: "Case", key: TableDataMap.CASE_ID, sortable: false },
    { title: "Patient", key: TableDataMap.PATIENT_NAME },
    { title: "Category", key: TableDataMap.TELEMEDICINE_CATEGORY_ID },
    { title: "Created At", key: TableDataMap.CREATED_DATE },
    { title: "Updated At", key: TableDataMap.UPDATED_DATE },
    { title: "Completed At", key: TableDataMap.COMPLETED_AT },
    { title: "Status", key: TableDataMap.STATUS },
  ],
  {
    sortOrder,
    sortBy,
    dateKeys: [TableDataMap.CREATED_DATE, TableDataMap.UPDATED_DATE, TableDataMap.COMPLETED_AT],
  },
);

const defaultTablePreferences = {
  currentPage: 1,
  tablePageSize: 100,
  sortOrder: 'descend',
  sortBy: TableDataMap.COMPLETED_AT,
}

const AllCases = (props) => {
  const { title, type } = props;
  const [cases, setCases] = useState([]);
  const [totalCount, setTotalCount] = useState(0);
  const [isFetching, setIsFetching] = useState(false);
  const [showModal, setShowModal] = useState(false);
  const [searchValue, setSearchValue] = useState(null);
  const [caseListFilter, setCaseListFilter] = useState({});
  const { statesList, telemedicineCategoriesList } = useSelector(state => state.entities)
  const { getTablePreferences, updateTablePreferences } = useContext(TablePreferencesContext);
  const tablePreferences = getTablePreferences(type, defaultTablePreferences);

  const { currentPage, tablePageSize, sortOrder, sortBy } = tablePreferences;

  const columns = useMemo(() => createTableColumns(sortOrder, sortBy), [sortOrder, sortBy]);

  const fetchCases = useCallback(() => {
    const newTablePreferences = {
      currentPage,
      tablePageSize,
      sortOrder,
      sortBy: TableSortByMap[sortBy] || sortBy,
    };

    const tableFilterMap = createTableQueryParamMap(newTablePreferences)
    const caseFilterMap = createCaseFilterQueryParamMap(caseListFilter, hideFields);
    if (searchValue !== null) {
      caseFilterMap[CaseQueryParamMap.SEARCH_TERM] = searchValue;
    }

    setIsFetching(true);
    PhysicianAPI
      .getCaseList({ ...tableFilterMap, ...caseFilterMap })
      .then(res => {
        setCases(res.data.payload.data);
        setTotalCount(res.data.payload.count);
      })
      .catch((err) => {
        message.error('Unable to fetch cases');
        console.log('Unable to fetch cases ERROR: ', err);
      })
      .finally(() => setIsFetching(false));
  }, [searchValue, caseListFilter, currentPage, tablePageSize, sortOrder, sortBy]);

  useEffect(() => {
    fetchCases();
  }, [fetchCases])

  const handleIntialTableCount = () => {
    updateTablePreferences(type, { currentPage: 1, tablePageSize: 100 })
  };

  const handleOk = (filterData) => {
    setCaseListFilter(filterData);
    setShowModal(false);
    handleIntialTableCount();
  };

  const searchCase = (value) => {
    setSearchValue(value);
    handleIntialTableCount();
  };

  const handleCancel = () => {
    setCaseListFilter({});
    setShowModal(false);
    handleIntialTableCount();
  };

  const handleCancelCross = () => {
    setShowModal(false);
  };

  const onChangePage = (page) => {
    updateTablePreferences(type, { currentPage: page })
  };

  const onPageSizeChange = (_, size) => {
    updateTablePreferences(type, { currentPage: 1, tablePageSize: size })
  };

  const onChangeOrder = (_pagination, _filters, sorter) => {
    updateTablePreferences(type, {
      sortOrder: sorter.order || 'ascend',
      sortBy: sorter.field,
    });
  }

  const casesForTable = cases.map((c) => {
    return {
      key: c.caseId,
      rowKey: c.caseId,
      [TableDataMap.CASE_ID]: c.caseId,
      [TableDataMap.PATIENT_NAME]: c.patientName,
      [TableDataMap.TELEMEDICINE_CATEGORY_ID]: c.category,
      [TableDataMap.CREATED_DATE]: dateTimeFormatter(c.createdDate),
      [TableDataMap.UPDATED_DATE]: c.updatedDate ? dateTimeFormatter(c.updatedDate) : '',
      [TableDataMap.COMPLETED_AT]: c.completedAt ? dateTimeFormatter(c.completedAt) : '',
      [TableDataMap.STATUS]: c.status?.split(' ').map(capitalize).join(' '),
    }
  })

  return (
    <div className="consulted-case">
      <div className="waiting-room-case-list-part-heading">
        <h1>{title}</h1>
        <SearchAndFilterCases
          searchCase={searchCase}
          showModal={showModal}
          setShowModal={setShowModal}
          handleCancelCross={handleCancelCross}
          handleCancel={handleCancel}
          handleOk={handleOk}
          telemedicines={telemedicineCategoriesList}
          states={statesList}
          hideFields={hideFields}
        />
      </div>

      <div className="consulted-case-no-of-patient-list-data">
        <Spin
          size="large"
          spinning={isFetching}
          indicator={<LoadingOutlined spin />}
        >
          <div>
            <Table
              columns={columns}
              dataSource={casesForTable}
              onChange={onChangeOrder}
              pagination={{
                total: totalCount,
                showTotal: () => `Total ${totalCount} cases`,
                current: currentPage,
                pageSize: tablePageSize,
                pageSizeOptions: ["5", "10", "20", "50", "100"],
                showSizeChanger: true,
                onChange: onChangePage,
                onShowSizeChange: onPageSizeChange,
              }}
              scroll={{ x: 900 }}
              size="large"
              onRow={(record) => {
                return {
                  onClick: () => {
                    history.push(
                      `/dashboard/physician/caseview/${record.caseId}`,
                      { from: history.location.pathname.split('/').pop() }
                    );
                  },
                };
              }}
            />
          </div>
        </Spin>
      </div>
    </div>
  );
};

export default AllCases;
